/**
 * Reactive hook to check whether an AI feature flag is currently enabled.
 *
 *   const { enabled, loading } = useAiFlag('auto_translation');
 *
 * Caching strategy:
 *   - Module-level cache with 30s TTL, shared across all useAiFlag callers
 *     within the same tab.
 *   - Cross-tab signalling via BroadcastChannel('ai-flags') with a graceful
 *     localStorage 'storage' event fallback.  When the Cloud GM AI Control Hub
 *     toggles a flag, every other open tab is notified within ~50ms and any
 *     mounted TranslateButton (or other useAiFlag consumer) re-fetches and
 *     re-renders, even if it was already mounted before the toggle.
 *   - Periodic 20s safety re-check covers the rare case where a tab missed
 *     a broadcast (e.g. just woke up from background).
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { getAuthHeaders } from './authHeaders';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const TTL_MS = 30_000;
const POLL_MS = 20_000;
const CHANNEL_NAME = 'ai-flags';
const STORAGE_KEY = 'ai-flags-bust';

let _cache = null;          // { ts, byId: { id -> enabled } }
let _inflight = null;       // promise – prevents thundering-herd
const _listeners = new Set(); // intra-tab refetch triggers

// --- Cross-tab channel (BroadcastChannel preferred, storage event fallback)
let _bc = null;
if (typeof BroadcastChannel !== 'undefined') {
  try {
    _bc = new BroadcastChannel(CHANNEL_NAME);
    _bc.onmessage = () => { _cache = null; _notify(); };
  } catch {
    _bc = null;
  }
}
if (typeof window !== 'undefined') {
  // Fallback: storage events fire in OTHER tabs when key changes.
  window.addEventListener('storage', (e) => {
    if (e.key === STORAGE_KEY) {
      _cache = null;
      _notify();
    }
  });
}

function _notify() {
  for (const fn of _listeners) {
    try { fn(); } catch { /* ignore */ }
  }
}

async function _fetchFlags() {
  if (_inflight) return _inflight;
  _inflight = (async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/ai-control/flags`, {
        headers: getAuthHeaders(),
      });
      const byId = {};
      for (const f of res.data?.features || []) byId[f.id] = !!f.enabled;
      _cache = { ts: Date.now(), byId };
      return byId;
    } catch {
      // Network/auth errors: fail-OPEN so we don't accidentally hide every
      // AI button when the admin's token just expired (the per-request
      // backend gate is still the source of truth).
      _cache = { ts: Date.now(), byId: {} };
      return {};
    } finally {
      _inflight = null;
    }
  })();
  return _inflight;
}

/**
 * Drop the cache and broadcast to every other tab + every mounted consumer
 * in THIS tab so they all re-fetch the latest flag state.
 */
export function invalidateAiFlags() {
  _cache = null;
  // Notify subscribers in THIS tab
  _notify();
  // Notify OTHER tabs via BroadcastChannel
  if (_bc) {
    try { _bc.postMessage({ ts: Date.now() }); } catch { /* ignore */ }
  }
  // Fallback signal via storage (writing the same key with a new value
  // fires the 'storage' event in other tabs)
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(STORAGE_KEY, String(Date.now()));
    }
  } catch { /* ignore quota / disabled storage */ }
}

export function useAiFlag(flagId, { defaultEnabled = true } = {}) {
  const [state, setState] = useState(() => {
    if (_cache && Date.now() - _cache.ts < TTL_MS && flagId in _cache.byId) {
      return { enabled: _cache.byId[flagId], loading: false };
    }
    return { enabled: defaultEnabled, loading: true };
  });

  useEffect(() => {
    let alive = true;

    const load = async () => {
      if (_cache && Date.now() - _cache.ts < TTL_MS) {
        if (alive) {
          setState({
            enabled: flagId in _cache.byId ? _cache.byId[flagId] : defaultEnabled,
            loading: false,
          });
        }
        return;
      }
      const byId = await _fetchFlags();
      if (!alive) return;
      setState({
        enabled: flagId in byId ? byId[flagId] : defaultEnabled,
        loading: false,
      });
    };

    load();
    _listeners.add(load);

    // Periodic safety re-check (covers missed broadcasts)
    const interval = setInterval(() => { _cache = null; load(); }, POLL_MS);

    return () => {
      alive = false;
      _listeners.delete(load);
      clearInterval(interval);
    };
  }, [flagId, defaultEnabled]);

  return state;
}
